import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, sendPasswordResetEmail } from 'firebase/auth';
import { auth, callableFunctions } from '../firebase.js';
import { countries } from '../constants/appConfig';
import { CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react'; // Importamos iconos para el feedback
import Swal from 'sweetalert2';
import '../styles/Login.css'; 
import logo from '../assets/logo192.png';

// Logo arriba del login: oculto desde 2026-10-26 a pedido de Nicolás (ver BITACORA.md).
// Para volver a mostrarlo, poner en true.
const SHOW_LOGIN_LOGO = false;

// --- ¡NUEVO! TEMA PERSONALIZADO PARA SWEETALERT2 ---
// Creamos una instancia de Swal con nuestras clases y estilos personalizados.
export const ThemedSwal = Swal.mixin({
    customClass: {
        popup: 'lignums-popup',
        title: 'lignums-title',
        htmlContainer: 'lignums-text',
        confirmButton: 'lignums-button lignums-confirm-button',
        cancelButton: 'lignums-button lignums-cancel-button',
        icon: 'lignums-icon',
        input: 'lignums-input' // Añadimos una clase para el input
    },
    buttonsStyling: false // Desactivamos los estilos por defecto de los botones para usar los nuestros.
});

const PasswordStrengthMeter = ({ password }) => {
    // --- LÓGICA DE FORTALEZA CORREGIDA ---
    const checkPasswordStrength = (pass) => {
        if (!pass || pass.length === 0) return 0;

        let score = 0;
        const checks = {
            length: pass.length >= 8,
            hasUpper: /[A-Z]/.test(pass),
            hasLower: /[a-z]/.test(pass),
            hasNumber: /[0-9]/.test(pass),
            hasSymbol: /[^A-Za-z0-9]/.test(pass)
        };

        // Si no cumple la longitud mínima, es muy débil.
        if (!checks.length) return 0;

        // Punto base por longitud mínima.
        score = 1;

        // Puntos por diversidad de caracteres.
        let diversity = 0;
        if (checks.hasUpper) diversity++;
        if (checks.hasLower) diversity++;
        if (checks.hasNumber) diversity++;
        if (checks.hasSymbol) diversity++;

        if (diversity >= 3) score++;
        if (diversity >= 4) score++;

        // Punto extra por longitud mayor.
        if (pass.length >= 12) score++;

        return Math.min(score, 4); // El score máximo es 4.
    };

    const strength = useMemo(() => checkPasswordStrength(password), [password]);
    const strengthLabel = ['Muy Débil', 'Débil', 'Media', 'Fuerte', 'Muy Fuerte']; // 5 niveles de etiqueta
    const strengthColor = ['#e74c3c', '#e67e22', '#f1c40f', '#2ecc71', '#27ae60'];

    return (
        <div className="password-strength-meter">
            <div className="strength-bar" style={{ width: `${(strength / 4) * 100}%`, backgroundColor: strengthColor[strength] || '#ddd' }}></div>
            <span className="strength-label" style={{ color: strengthColor[strength] || '#888' }}>
                {password.length > 0 ? strengthLabel[strength] : ''}
            </span>
        </div>
    );
};

// --- ¡NUEVO! FUNCIÓN PARA TRADUCIR ERRORES DE FIREBASE ---
const getFriendlyErrorMessage = (error) => {
    // Si el error viene de una Cloud Function (onCall)
    if (error.code && error.details) {
        switch (error.code) {
            case 'invalid-argument':
                return error.details.message || 'Los datos enviados no son válidos. Por favor, revisa los campos.';
            case 'already-exists':
                return error.details.message || 'El nombre de usuario o correo ya está en uso.';
            case 'permission-denied':
                return 'No tienes permiso para realizar esta acción.';
            default:
                return 'Ocurrió un error en el servidor. Por favor, contacta a soporte.';
        }
    }
    // Si el error viene de Firebase Authentication (cliente)
    switch (error.code) {
        case 'auth/email-already-in-use': return 'El correo electrónico ya está registrado.';
        case 'auth/invalid-email': return 'El formato del correo electrónico no es válido.';
        case 'auth/wrong-password': return 'La contraseña es incorrecta. Inténtalo de nuevo.';
        case 'auth/user-not-found': return 'No se encontró una cuenta con este correo electrónico.';
        default: return error.message || 'Ocurrió un error inesperado. Inténtalo de nuevo.';
    }
};

// --- ¡NUEVO! ASISTENTE DE BIENVENIDA ---
// Asistente de bienvenida: en pausa desde 2026-10-23, porque las cuentas nuevas quedan pendientes
// de aprobación y no ven el panel hasta que un admin las apruebe (ver BITACORA.md).
// eslint-disable-next-line no-unused-vars
const showWelcomeWizard = async () => {
    // --- PASO 1: Explicar el Saldo Retirable ---
    const step1Result = await ThemedSwal.fire({
        icon: 'info',
        title: 'Paso 1: Tu Saldo',
        html: 'Todos tus resultados y comisiones se acumularán en tu <strong>Disponible para retiro</strong>. Desde allí podrás solicitar tus retiros.',
        confirmButtonText: 'Entendido, ¡siguiente paso!',
        showClass: { popup: 'lignums-fade-in-up-animation' },
        hideClass: { popup: 'lignums-fade-out-down-animation' }
    });

    // Si el usuario cierra el popup sin confirmar, salimos del asistente.
    if (!step1Result.isConfirmed) {
        return;
    }

    // --- PASO 2: Configurar la Billetera ---
    const step2Result = await ThemedSwal.fire({
        icon: 'warning',
        title: 'Paso 2: ¡Acción Importante!',
        html: 'Para poder recibir tus pagos, es fundamental que configures tu dirección de billetera <strong>USDT (TRC20)</strong> en tu perfil.',
        confirmButtonText: '¡Llévame a mi perfil!',
        showCancelButton: true,
        cancelButtonText: 'Lo haré más tarde',
        showClass: { popup: 'lignums-fade-in-up-animation' },
        hideClass: { popup: 'lignums-fade-out-down-animation' }
    });

    if (step2Result.isConfirmed) {
        window.location.href = '/dashboard/perfil'; 
    }
};

const AuthFlow = () => {
    const location = useLocation();

    // --- LÓGICA MEJORADA: Se lee la URL de forma síncrona para evitar parpadeos ---
    const urlParams = new URLSearchParams(location.search);
    const refCodeFromUrl = urlParams.get('ref');

    // LÓGICA CORREGIDA: La vista de registro se muestra si la ruta es /register O si hay un código de referido.
    // De lo contrario, se muestra la vista de login. Esto fuerza la vista de registro de forma definitiva.
    const [isLoginView, setIsLoginView] = useState(location.pathname !== '/register' && !refCodeFromUrl);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [username, setUsername] = useState('');
    const [fullName, setFullName] = useState('');
    const [country, setCountry] = useState('');
    // El código de referido se establece desde la URL o se deja vacío.
    // --- CORRECCIÓN: El estado del código de referido se maneja aquí ---
    const [referralCode, setReferralCode] = useState(refCodeFromUrl || '');
    const [usernameStatus, setUsernameStatus] = useState('idle'); // 'idle', 'checking', 'available', 'taken', 'too_short', 'invalid_chars', 'error'
    const [referralCodeStatus, setReferralCodeStatus] = useState(refCodeFromUrl ? 'checking' : 'idle'); // idle, checking, valid, invalid, error
    // --- NUEVO: useEffect para validación de nombre de usuario en tiempo real ---
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        // Si el campo de usuario está vacío
        if (username.length === 0) {
            setUsernameStatus('idle');
            return;
        }
        // Mostrar error si es menor a 5 caracteres (coincide con backend)
        if (username.length < 5) {
            setUsernameStatus('too_short');
            return;
        }
        const usernameRegex = /^[a-zA-Z0-9_.]+$/;
        if (!usernameRegex.test(username)) {
            setUsernameStatus('invalid_chars');
            return;
        }

        // Debounce: esperar 500ms después de que el usuario deje de escribir
        const debounceTimeout = setTimeout(async () => {
            setUsernameStatus('checking');
            try {
                const result = await callableFunctions.checkUsernameAvailability({ username });
                if (result.data.isAvailable) {
                    setUsernameStatus('available');
                } else {
                    setUsernameStatus('taken');
                }
            } catch (err) {
                console.error("Error al verificar username:", err);
                setUsernameStatus('error');
            }
        }, 500);
        return () => clearTimeout(debounceTimeout);
    }, [username]);

    // --- NUEVO: useEffect para validación de código de referido en tiempo real ---
    useEffect(() => {
        const validateCode = async (code) => {
            if (!code) {
                setReferralCodeStatus('idle');
                return;
            }
            setReferralCodeStatus('checking');
            try {
                const result = await callableFunctions.checkReferralCodeValidity({ referralCode: code });
                setReferralCodeStatus(result.data.isValid ? 'valid' : 'invalid');
            } catch (err) {
                console.error("Error al verificar código de referido:", err);
                setReferralCodeStatus('error');
            }
        };

        // Si el código viene de la URL, lo validamos una vez al montar.
        if (refCodeFromUrl) {
            validateCode(refCodeFromUrl);
            return; // No hacer nada más porque el campo es de solo lectura.
        }

        // Si el usuario está escribiendo, aplicar debounce.
        if (referralCode.length === 0) {
            setReferralCodeStatus('idle');
            return;
        }

        const debounceTimeout = setTimeout(() => validateCode(referralCode), 500);
        return () => clearTimeout(debounceTimeout);
    }, [referralCode, refCodeFromUrl]);

    const handleRegister = async (e) => {
        e.preventDefault();
        // --- REEMPLAZO DE setError POR Swal.fire ---
        if (!email || !password || !confirmPassword || !username || !fullName || !country || !referralCode) {
            ThemedSwal.fire({ icon: 'warning', title: 'Campos Incompletos', text: 'Por favor, rellena todos los campos obligatorios.' });
            return;
        }
        if (password !== confirmPassword) {
            ThemedSwal.fire({ icon: 'error', title: 'Oops...', text: 'Las contraseñas no coinciden.' });
            return;
        }
        if (usernameStatus !== 'available') {
            ThemedSwal.fire({ icon: 'warning', title: 'Atención', text: 'Por favor, elige un nombre de usuario válido y disponible.' });
            return;
        }
        // --- NUEVO: Validar el estado del código de referido antes de registrar ---
        if (referralCodeStatus !== 'valid') { // Si se ha introducido un código, debe ser válido. Si está vacío, se permite.
             ThemedSwal.fire({ icon: 'warning', title: 'Código de Referido Inválido', text: 'El ID de referencia que ingresaste no es válido. Por favor, corrígelo.' });
             return;
        }
        if (password.length < 6) {
            ThemedSwal.fire({ icon: 'warning', title: 'Contraseña Débil', text: 'La contraseña debe tener al menos 6 caracteres.' });
            return;
        }
        setLoading(true);
        try {
            // 1. Crear el usuario en Firebase Authentication
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 2. Llamar a la Cloud Function para crear el perfil en Firestore

            // --- LOG DE DEPURACIÓN ---
            // Creamos el objeto de datos y lo mostramos en la consola antes de enviarlo.
            const profileDataToSend = {
                uid: user.uid,
                email: email,
                username: username,
                fullName: fullName,
                country: country,
                referralCode: referralCode,
                termsAccepted: false, // Términos desactivados (ver BITACORA.md, 2026-10-21): nadie los acepta al registrarse
            };
            console.log('Enviando datos a createUserProfile:', profileDataToSend);
            
            // --- CORRECCIÓN: Usar la función correcta desde callableFunctions ---
            const result = await callableFunctions.createUserProfile(profileDataToSend);

            // --- NUEVA LÓGICA DE NOTIFICACIÓN ---
            // Verificamos el estado del referido devuelto por el backend.
            // Las cuentas nuevas quedan pendientes hasta que un admin las apruebe (Aprobación de Usuarios).
            const referralWarning = (result.data.referralStatus === 'not_found' || result.data.referralStatus === 'referrer_not_found')
                ? "<br/><br/><strong>Aviso:</strong> El código de referido que ingresaste no es válido o no se encontró. Has sido registrado sin un patrocinador."
                : '';
            setTimeout(() => {
                ThemedSwal.fire({ // Este es un aviso importante, no lo cerramos automáticamente.
                    title: 'Registro recibido',
                    html: "Tu cuenta fue creada y quedó <strong>pendiente de aprobación</strong>. Te habilitaremos el acceso una vez que el equipo de Lignums la revise." + referralWarning,
                    icon: referralWarning ? 'warning' : 'success',
                    confirmButtonText: 'Entendido'
                });
            }, 1000);

        } catch (err) {
            console.error("Error en el registro:", err);
            // El backend ahora se encarga de eliminar el usuario de Auth si la creación del perfil falla.
            // Por lo tanto, ya no es necesario hacerlo aquí en el cliente.
            // Simplemente mostramos el error.
            ThemedSwal.fire({ icon: 'error', title: 'Error de Registro', text: getFriendlyErrorMessage(err) });
        } finally { setLoading(false); }
    };
    
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            console.error("Error en el inicio de sesión:", err);
            // --- REEMPLAZO DE setError POR Swal.fire ---
            ThemedSwal.fire({ icon: 'error', title: 'Error al Iniciar Sesión', text: getFriendlyErrorMessage(err) });
        } finally { setLoading(false); }
    };

    const handleGoogleSignIn = async () => {
        setLoading(true);
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (err) {
            // --- REEMPLAZO DE setError POR Swal.fire ---
            ThemedSwal.fire({ icon: 'error', title: 'Error', text: 'No se pudo iniciar sesión con Google. Inténtalo de nuevo.' });
            console.error("Error con Google Sign In:", err);
        } finally { setLoading(false); }
    };
    
    const handlePasswordReset = () => {
        if (!email) {
            ThemedSwal.fire({ icon: 'info', title: 'Atención', text: 'Por favor, ingresa tu correo electrónico en el campo correspondiente para restablecer tu contraseña.' });
            return;
        }
        setLoading(true);
        sendPasswordResetEmail(auth, email)
            .then(() => {
                // --- MODIFICACIÓN: Añadimos timer y timerProgressBar ---
                ThemedSwal.fire({
                    icon: 'success',
                    title: 'Correo Enviado',
                    text: 'Se ha enviado un enlace para restablecer tu contraseña a tu correo electrónico.',
                    timer: 3000, // El popup se cerrará en 3 segundos
                    timerProgressBar: true // Muestra la barra de progreso
                });
            })
            .catch((err) => {
                ThemedSwal.fire({ icon: 'error', title: 'Error', text: 'No se pudo enviar el correo. Verifica que la dirección sea correcta.' });
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    {SHOW_LOGIN_LOGO && (
                        <img
                            src={logo}
                            alt="Lignums Group Logo"
                            className="login-logo"
                            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                        />
                    )}
                    <h2>{isLoginView ? 'Iniciar Sesión' : 'Crear una cuenta gratis'}</h2>
                </div>
                {isLoginView && (
                    <div className="internal-use-notice" role="note">
                        <strong>Herramienta de uso interno exclusivo</strong>
                        <span>Acceso reservado a usuarios autorizados por Lignums.</span>
                    </div>
                )}
                <form onSubmit={isLoginView ? handleLogin : handleRegister}>
                    {!isLoginView && (
                        <>
                            <div className="input-group with-validation">
                                <label htmlFor="username">Nombre de Usuario</label>
                                <div className="input-wrapper">
                                    <input 
                                        type="text" 
                                        id="username" 
                                        value={username} 
                                        onChange={(e) => {
                                            // Eliminar espacios en blanco en tiempo real
                                            const value = e.target.value.replace(/\s+/g, '');
                                            setUsername(value);
                                        }} 
                                        onPaste={e => {
                                            // Evitar pegar espacios
                                            const pasted = e.clipboardData.getData('text').replace(/\s+/g, '');
                                            e.preventDefault();
                                            setUsername(pasted);
                                        }}
                                        required 
                                        minLength="5" 
                                        placeholder="Mínimo 5 caracteres"
                                    />
                                    <div className="validation-icon">
                                        {usernameStatus === 'checking' && <Loader2 size={18} className="spinner" />}
                                        {usernameStatus === 'available' && <CheckCircle size={18} className="icon-success" />}
                                        {usernameStatus === 'taken' && <XCircle size={18} className="icon-error" />}
                                    </div>
                                </div>
                                {/* --- MENSAJE DE VALIDACIÓN MEJORADO --- */}
                                <div className={`validation-message ${usernameStatus}`}>
                                    {usernameStatus === 'too_short' && 'El nombre de usuario debe tener al menos 5 caracteres.'}
                                    {usernameStatus === 'invalid_chars' && 'No puede contener espacios ni caracteres especiales (solo letras, números, puntos y guiones bajos).'}
                                    {usernameStatus === 'available' && 'Nombre de usuario disponible'}
                                    {usernameStatus === 'taken' && 'Este nombre de usuario ya está en uso.'}
                                    {usernameStatus === 'error' && 'No se pudo verificar. Intenta de nuevo.'}
                                </div>
                            </div>
                            <div className="input-group"><label htmlFor="fullName">Nombre Completo</label><input type="text" id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} required /></div>
                            <div className="input-group"><label htmlFor="country">País</label><select id="country" value={country} onChange={(e) => setCountry(e.target.value)} required><option value="" disabled>Selecciona tu país</option>{countries.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
                        </>
                    )}
                    <div className="input-group"><label htmlFor="email">Correo electrónico</label><input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
                    <div className="input-group">
                        <label htmlFor="password">Contraseña</label>
                        <div className="input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => setShowPassword(v => !v)}
                                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    {!isLoginView && (
                        <>
                            <PasswordStrengthMeter password={password} />
                            <div className="input-group"><label htmlFor="confirmPassword">Confirmar Contraseña</label><input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required /></div>
                            <div className="input-group with-validation">
                                <label htmlFor="referral">ID de Referencia (Obligatorio)</label>
                                <div className="input-wrapper">
                                    <input
                                        type="text"
                                        id="referral"
                                        value={referralCode}
                                        onChange={(e) => setReferralCode(e.target.value)}
                                        placeholder="Código de quien te invitó"
                                        readOnly={!!refCodeFromUrl}
                                        required
                                    />
                                    <div className="validation-icon">
                                        {referralCodeStatus === 'checking' && <Loader2 size={18} className="spinner" />}
                                        {referralCodeStatus === 'valid' && <CheckCircle size={18} className="icon-success" />}
                                        {(referralCodeStatus === 'invalid' || referralCodeStatus === 'error') && <XCircle size={18} className="icon-error" />}
                                    </div>
                                </div>
                                <div className={`validation-message ${referralCodeStatus}`}>
                                    {referralCodeStatus === 'invalid' && 'El código de referido no es válido.'}
                                    {referralCodeStatus === 'error' && 'No se pudo verificar el código.'}
                                </div>
                            </div>
                        </>
                    )}
                    {/* --- BOTÓN DE SUBMIT CON LÓGICA DE DESACTIVACIÓN MEJORADA --- */}
                    <button 
                        type="submit" 
                        disabled={loading || (!isLoginView && (
                            usernameStatus !== 'available' ||
                            referralCodeStatus !== 'valid'
                        ))} 
                        className="login-button"
                    >{loading ? 'Procesando...' : (isLoginView ? 'Iniciar Sesión' : 'Crear cuenta')}</button>

                </form>

                {isLoginView && (
                    <div className="forgot-password-link" onClick={handlePasswordReset}>
                        ¿Olvidaste tu contraseña?
                    </div>
                )}
                
                <div className="divider">O</div>
                
                <button onClick={handleGoogleSignIn} disabled={loading} className="google-signin-button">
                    <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56,12.25C22.56,11.42,22.49,10.62,22.36,9.83H12V14.45H18.05C17.76,16.14,16.89,17.59,15.5,18.58V21.3H19.5C21.56,19.23,22.56,16.03,22.56,12.25Z" fill="#4285F4"/>
                        <path d="M12,23C14.97,23,17.45,22.04,19.5,20.46L15.5,17.68C14.53,18.36,13.36,18.77,12,18.77C9.18,18.77,6.8,16.9,5.83,14.39L1.73,14.39V17.2C3.68,20.86,7.5,23,12,23Z" fill="#34A853"/>
                        <path d="M5.83,14.39C5.6,13.76,5.46,13.1,5.46,12.41C5.46,11.72,5.6,11.06,5.83,10.43V7.62L1.73,7.62C0.95,9.13,0.5,10.72,0.5,12.41C0.5,14.1,0.95,15.69,1.73,17.2L5.83,14.39Z" fill="#FBBC05"/>
                        <path d="M12,6.23C13.44,6.23,14.73,6.72,15.73,7.66L19.59,3.95C17.45,2.04,14.97,1,12,1C7.5,1,3.68,3.14,1.73,6.79L5.83,9.57C6.8,7.01,9.18,5.23,12,5.23Z" fill="#EA4335"/>
                    </svg>
                    <span>{isLoginView ? 'Iniciar sesión con Google' : 'Registrarse con Google'}</span>
                </button>

                {/* Registro oculto (2026-10-23, ver BITACORA.md): desde el login ya no hay link a
                    "Crear una cuenta". El registro sigue funcionando por /register y los enlaces
                    de referido (?ref=). */}
                {!isLoginView && (
                    <div className="auth-toggle">
                        <p>¿Ya estás registrado? <span onClick={() => setIsLoginView(true)}>Iniciar sesión</span></p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthFlow;